import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Menu, X, Mail, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import MusicPlayer from '../player/MusicPlayer';
import PWAInstallPrompt from '../PWAInstallPrompt';
import CartButton from '../cart/CartButton';
import ShoppingCart from '../cart/ShoppingCart';

export default function Layout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/music', label: 'Music' },
    { path: '/shows', label: 'Shows' },
    { path: '/merch', label: 'Merch' },
    { path: '/press', label: 'Press' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-black">
      {/* Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50">
        <nav className="container mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo - Left side */}
            <Link to="/">
              <img
                src="/images/mainlogo.png"
                alt="Scooby DaGodSon"
                className="h-28 sm:h-24 md:h-36 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation - Center pill */}
            <div className="hidden md:flex items-center gap-5 bg-black/80 backdrop-blur-sm rounded-full px-5 py-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-semibold transition-colors ${
                    isActive(link.path)
                      ? 'text-orange-500'
                      : 'text-white hover:text-orange-400'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Desktop Actions - Right side pill */}
            <div className="hidden md:flex items-center gap-2 bg-black/80 backdrop-blur-sm rounded-full px-3 py-2">
              <CartButton size="sm" />
              <Link to="/contact">
                <Button variant="outline" size="sm" className="border border-white text-white hover:bg-white hover:text-black font-semibold text-sm px-4 bg-transparent">
                  Book Now
                </Button>
              </Link>
            </div>

            {/* Mobile Actions */}
            <div className="flex md:hidden items-center gap-3 bg-black/80 backdrop-blur-sm rounded-full px-4 py-2">
              <CartButton />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white p-1"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-black border-t border-gray-800"
            >
              <div className="container mx-auto px-6 py-4 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`block font-semibold transition-colors ${
                      isActive(link.path)
                        ? 'text-orange-500'
                        : 'text-white hover:text-orange-400'
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold">
                    Book Now
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="pb-32">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] text-white py-10 px-6 border-t border-gray-800">
        <div className="container mx-auto">
          {/* Contact Info */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-8">
            <a
              href="mailto:siriuscompanydc@gmail.com"
              className="flex items-center gap-2 text-gray-300 hover:text-orange-500 transition-colors"
            >
              <Mail className="w-5 h-5" />
              siriuscompanydc@gmail.com
            </a>
            <a
              href="tel:+12027431267"
              className="flex items-center gap-2 text-gray-300 hover:text-orange-500 transition-colors"
            >
              <Phone className="w-5 h-5" />
              (202) 743-1267
            </a>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between pt-6 border-t border-gray-800">
            <div className="flex items-center gap-2 mb-4 md:mb-0">
              <span className="text-gray-400 text-sm">© 2024 Scooby DaGodSon. All rights reserved.</span>
            </div>

            <div className="flex gap-8">
              <Link to="/privacy" className="text-gray-400 hover:text-orange-500 transition-colors">Privacy</Link>
              <Link to="/terms" className="text-gray-400 hover:text-orange-500 transition-colors">Terms</Link>
              <Link to="/press" className="text-gray-400 hover:text-orange-500 transition-colors">Press Kit</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Music Player */}
      <MusicPlayer />

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Shopping Cart */}
      <ShoppingCart />
    </div>
  );
}
